import { memo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

interface OperatorInputsEditorProps {
  componentInputs: Record<string, Record<string, string>>;
  busy: boolean;
  onSave: (componentName: string, inputs: Record<string, string>) => Promise<boolean>;
}

interface Row {
  key: string;
  value: string;
}

function toRows(inputs: Record<string, string>): Row[] {
  const rows = Object.entries(inputs).map(([key, value]) => ({ key, value }));
  return rows.length > 0 ? rows : [{ key: '', value: '' }];
}

const OperatorInputsEditor = memo<OperatorInputsEditorProps>(({ componentInputs, busy, onSave }) => {
  const [draft, setDraft] = useState<Record<string, Row[]>>(() => {
    const initial: Record<string, Row[]> = {};
    Object.entries(componentInputs).forEach(([component, inputs]) => {
      initial[component] = toRows(inputs);
    });
    return initial;
  });
  const [newComponent, setNewComponent] = useState('');

  const setRows = (component: string, rows: Row[]) =>
    setDraft((prev) => ({ ...prev, [component]: rows }));

  const addComponent = () => {
    const name = newComponent.trim();
    if (!name || draft[name]) return;
    setDraft((prev) => ({ ...prev, [name]: [{ key: '', value: '' }] }));
    setNewComponent('');
  };

  const handleSave = (component: string) => {
    const rows = draft[component] ?? [];
    const inputs: Record<string, string> = {};
    rows.forEach(({ key, value }) => {
      const trimmed = key.trim();
      if (trimmed) inputs[trimmed] = value;
    });
    onSave(component, inputs);
  };

  const components = Object.keys(draft);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Operator-supplied values per component, keyed by CloudFormation parameter.
      </Typography>

      {components.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No operator inputs configured. Add a component below to supply values.
        </Typography>
      )}

      {components.map((component) => {
        const rows = draft[component];
        return (
          <Box key={component} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'monospace' }}>{component}</Typography>
            {rows.map((row, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Parameter"
                  value={row.key}
                  onChange={(e) => setRows(component, rows.map((r, i) => (i === idx ? { ...r, key: e.target.value } : r)))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={row.value}
                  onChange={(e) => setRows(component, rows.map((r, i) => (i === idx ? { ...r, value: e.target.value } : r)))}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove parameter"
                  onClick={() => setRows(component, rows.filter((_, i) => i !== idx))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => setRows(component, [...rows, { key: '', value: '' }])}
              >
                Add parameter
              </Button>
              <Button size="small" variant="contained" disabled={busy} onClick={() => handleSave(component)}>
                Save
              </Button>
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Component name"
          value={newComponent}
          onChange={(e) => setNewComponent(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button size="small" startIcon={<AddIcon fontSize="small" />} disabled={!newComponent.trim()} onClick={addComponent}>
          Add component
        </Button>
      </Box>
    </Box>
  );
});

OperatorInputsEditor.displayName = 'OperatorInputsEditor';
export default OperatorInputsEditor;
